import React, { useEffect, useState } from 'react';
import Button from '../Button/Button';
import { Icon } from '../Icon/Icon';
import Select from '../Select/Select';
import Option from '../Select/Option/Option';
import { useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import ProgressBar from '../ProgressBar/ProgressBar';

const useStyles = createUseStyles({
  root: {
    width: '100%',
    height: '100%',
    tableLayout: (props) => props.tableLayout,
    marginBottom: '64px',
  },
  thead__tr: {
    backgroundColor: 'var(--fifth-color)',
  },
  tfoot__tr: {
    backgroundColor: 'var(--fifth-color)',
  },
  tbody__tr: {
    '&:nth-child(odd)': {
      backgroundColor: 'var(--background-color)', // Color for odd rows
    },
    '&:nth-child(even)': {
      backgroundColor: 'var(--fifth-color)', // Color for odd rows
    },
    '&:hover': {
      backgroundColor: 'var(--alert-notification-backgroundColor)',
      cursor: 'pointer',
    },
  },
  th: {
    userSelect: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textAlign: 'left',
    padding: 10,
  },
  th__min: {
    width: '1px',
  },
  td: {
    marginRight: 5,
    padding: 5,
  },
  td__min: {
    width: '1px',
  },
  td__loading: {
    width: '100%',
    height: '100%',
    textAlign: 'center',
    verticalAlign: 'center',
  },
});

export const DataTable = ({
  data,
  columns,
  order = [],
  loading,
  showProgress,
  progress,
  onRowClick,
  onRowContextMenu,
  onSort,
  onPaginate,
  onLimitChange,
  paginatePrevDisabled,
  paginateNextDisabled,
  limitDisabled,
  limits = [25, 50, 100, 250],
  defaultLimit = 25,
  footerVisible = true,
  tableLayout,
  ...props
}) => {
  const classes = useStyles({ tableLayout });
  const [limitValue, setLimitValue] = useState(defaultLimit);

  useEffect(() => {
    if (onLimitChange) {
      onLimitChange({ limit: limitValue });
    }
  }, [onLimitChange, limitValue]);

  const handleSort = (key, dir) => {
    if (onSort && key) {
      onSort(key, dir);
    }
  };

  const handlePageChange = (page) => {
    if (onPaginate) {
      onPaginate({ page });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <table border={0} cellPadding={0} cellSpacing={0} className={classes.root} {...props}>
        <thead>
          <tr>
            <th colSpan={columns.length}>
              <div style={{ display: 'flex' }}></div>
              {showProgress && <ProgressBar value={progress}></ProgressBar>}
            </th>
          </tr>
          <tr className={classes.thead__tr}>
            {columns
              ? columns.map((col) => {
                  const label = col.label ? col.label : renderLabel(col.key);
                  const style = { userSelect: 'none', cursor: 'pointer' };
                  return (
                    <th
                      key={col.key}
                      style={style}
                      className={`${classes.th} ${col.minWidth && classes.th__min}`}
                    >
                      {col.sortable ? (
                        <SortArrow
                          id={col.key}
                          onSort={handleSort}
                          direction={order.find((subArr) => subArr[0] === col.key)?.[1] ?? 'none'}
                        >
                          {label}
                        </SortArrow>
                      ) : (
                        label
                      )}
                    </th>
                  );
                })
              : Object.keys(data[0] ?? []).map((key) => (
                  <th key={key}>
                    <SortArrow id={key} onSort={handleSort}>
                      {renderLabel(key)}
                    </SortArrow>
                  </th>
                ))}
          </tr>
        </thead>
        <tbody>
          {loading || data.length === 0 ? (
            <tr className={classes.tbody__tr}>
              <td colSpan={columns.length} className={classes.td__loading}>
                {data.length === 0 && !loading ? 'No data' : 'Loading...'}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={classes.tbody__tr}
                onMouseUp={(event) => {
                  onRowClick && onRowClick(event, row, rowIndex);
                }}
                onContextMenu={(event) => {
                  onRowContextMenu && onRowContextMenu(event, row, rowIndex);
                }}
              >
                {columns
                  ? columns.map((col) => {
                      const value = row[col.key];
                      return (
                        <td
                          key={col.key}
                          className={`${classes.td} ${col.minWidth ? classes.td__min : ''}`}
                        >
                          {col.render ? col.render(value, row) : defaultRenderer(value)}
                        </td>
                      );
                    })
                  : Object.keys(row).map((key) => <td key={key}>{row[key]}</td>)}
              </tr>
            ))
          )}
          <tr>
            <td colSpan={columns.length} style={{ height: '100%' }}>
              {/*filler cell*/}
            </td>
          </tr>
        </tbody>
      </table>
      {footerVisible && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button onClick={() => handlePageChange(-1)} disabled={paginatePrevDisabled} circle>
            <Icon>navigate_before</Icon>
          </Button>
          <Select
            style={{ display: 'inline-block' }}
            placeholder={'Limit'}
            onChange={(e) => setLimitValue(e.value)}
            value={limitValue}
            disabled={limitDisabled}
          >
            {limits &&
              limits.map((limit) => (
                <Option key={limit} value={limit}>
                  {limit}
                </Option>
              ))}
          </Select>
          <Button onClick={() => handlePageChange(1)} disabled={paginateNextDisabled} circle>
            <Icon>navigate_next</Icon>
          </Button>
        </div>
      )}
    </div>
  );
};

const defaultRenderer = (value) => `${value}`;

const renderLabel = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const SortArrow = ({ id, direction = 'none', onSort, children, ...props }) => {
  const handleSort = useCallback(() => {
    let nextSort;
    if (direction === 'none') nextSort = 'asc';
    else if (direction === 'asc') nextSort = 'desc';
    else if (direction === 'desc') nextSort = 'none';

    //setSortDirection(nextSort);
    onSort && onSort(id, nextSort);
  }, [id, direction, onSort]);

  const classes = {
    sortNone: 'sort-none',
    sortAsc: 'sort-asc',
    sortDesc: 'sort-desc',
  };

  return (
    <div
      onClick={handleSort}
      style={{ display: 'flex', justifyContent: 'space-between' }}
      {...props}
    >
      <span style={{ display: 'flex', alignItems: 'center' }}>{children}</span>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {direction === 'asc' && (
          <i className={classes.sortAsc + ' material-icons'}>arrow_drop_up</i>
        )}
        {direction === 'desc' && (
          <i className={classes.sortDesc + ' material-icons'}>arrow_drop_down</i>
        )}
        {direction === 'none' && <i className={classes.sortNone + ' material-icons'}>sort</i>}
      </span>
    </div>
  );
};
